import {
  type FriendDto,
  type InviteFriendResponse,
  type TournamentDto,
  lastSeenText,
} from '@penalty-cup/shared';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../api.js';
import { useApp } from '../store.js';
import { Jersey } from './Jersey.js';

const REFRESH_MS = 15_000;

const SENT: Record<InviteFriendResponse['delivered'], string> = {
  game: 'Позван ✓',
  telegram: 'Написали ✓',
  failed: 'Не дошло',
};

/** Друзья в игре в лобби: фильтр «в сети», вызов одним нажатием. */
export function FriendsPicker({ tournament }: { tournament: TournamentDto }) {
  const token = useApp((s) => s.token);
  // «был N мин назад» считаем от момента загрузки списка (обновляется каждые 15 с)
  const [now, setNow] = useState(() => Date.now());
  const [friends, setFriends] = useState<FriendDto[] | null>(null);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [sent, setSent] = useState<Record<number, string>>({});

  const load = useCallback(() => {
    if (!token) return () => undefined;
    let alive = true;
    api.friends(token).then(
      (d) => {
        if (!alive) return;
        setFriends(d.friends);
        setNow(Date.now());
      },
      () => alive && setFriends((f) => f ?? []),
    );
    return () => {
      alive = false;
    };
  }, [token]);

  useEffect(() => {
    let stop = load();
    const timer = setInterval(() => {
      stop();
      stop = load();
    }, REFRESH_MS);
    return () => {
      stop();
      clearInterval(timer);
    };
  }, [load]);

  if (!friends) return null;
  const inside = new Set(tournament.players.map((p) => p.name));
  const list = friends.filter((f) => !inside.has(f.name) && (!onlineOnly || f.online));
  const onlineCount = friends.filter((f) => f.online).length;

  const invite = async (f: FriendDto) => {
    if (!token) return;
    setSent((s) => ({ ...s, [f.id]: '…' }));
    try {
      const r = await api.inviteFriend(token, tournament.id, f.id);
      setSent((s) => ({ ...s, [f.id]: SENT[r.delivered] }));
    } catch (err) {
      const code = err instanceof ApiError ? err.message : '';
      const text =
        code === 'already_invited' ? 'Уже позвали' : code === 'tournament_full' ? 'Мест нет' : 'Ошибка';
      setSent((s) => ({ ...s, [f.id]: text }));
    }
  };

  return (
    <section className="friends" aria-label="Друзья в игре">
      <div className="friends__head">
        <b>Друзья в игре</b>
        <div className="friends__filter" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={!onlineOnly}
            className={`chip chip--small ${!onlineOnly ? 'chip--on' : ''}`}
            onClick={() => setOnlineOnly(false)}
          >
            Все {friends.length}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={onlineOnly}
            className={`chip chip--small ${onlineOnly ? 'chip--on' : ''}`}
            onClick={() => setOnlineOnly(true)}
          >
            🟢 В сети {onlineCount}
          </button>
        </div>
      </div>
      {friends.length === 0 ? (
        <p className="field__hint">
          Здесь появятся друзья, с которыми вы сыграете по приглашению. Новых зовите кнопкой выше.
        </p>
      ) : list.length === 0 ? (
        <p className="field__hint">Сейчас никого нет в сети — позовите из «Все»: бот напишет им в Telegram</p>
      ) : (
        <ul className="friends__list">
          {list.map((f) => (
            <li key={f.id} className="friends__row">
              <Jersey kit={f.kit} className="friends__kit" />
              <span className="friends__name">
                {f.name}
                <small className={f.online ? (f.busy ? 'friends__busy' : 'friends__online') : ''}>
                  {f.online ? (f.busy ? 'играет турнир' : 'в сети') : lastSeenText(f.lastSeenAt, now)}
                </small>
              </span>
              <button
                type="button"
                className="chip chip--small"
                disabled={!!sent[f.id]}
                onClick={() => void invite(f)}
              >
                {sent[f.id] ?? 'Позвать'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
